import { useRef, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PinLogin({ onSuccess }) {
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const refs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  const submit = async (pin) => {
    if (pin.length < 4 || busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(typeof data.detail === "string" ? data.detail : "Login failed — try again");
        setDigits(["", "", "", ""]);
        refs[0].current?.focus();
        return;
      }
      localStorage.setItem("tripleo_admin_token", data.token);
      onSuccess();
    } catch {
      setError("Connection error — try again");
    } finally {
      setBusy(false);
    }
  };

  const setDigit = (i, v) => {
    const d = v.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[i] = d;
    setDigits(next);
    setError("");
    if (d && i < 3) refs[i + 1].current?.focus();
    if (d && i === 3) submit([...digits.slice(0, 3), d].join(""));
  };

  const onKeyDown = (i) => (e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) refs[i - 1].current?.focus();
    if (e.key === "Enter") submit(digits.join(""));
  };

  return (
    <div className="flex min-h-svh flex-col items-center justify-center bg-ink px-4">
      <div data-testid="admin-login" className="w-full max-w-sm border border-white/10 bg-panel p-8 sm:p-10">
        <img src="/images/logo.png" alt="Triple O Creative Industries" className="h-9 w-auto" />
        <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.35em] text-acid">
          Studio Access
        </p>
        <h1 className="mt-3 font-display text-2xl font-bold uppercase tracking-tight text-paper">
          Enter your 4-digit PIN
        </h1>
        <div className="mt-8 flex gap-3">
          {digits.map((d, i) => (
            <input
              key={i}
              ref={refs[i]}
              data-testid={`admin-pin-input-${i}`}
              type="password"
              inputMode="numeric"
              autoComplete="off"
              maxLength={1}
              value={d}
              onChange={(e) => setDigit(i, e.target.value)}
              onKeyDown={onKeyDown(i)}
              autoFocus={i === 0}
              aria-label={`PIN digit ${i + 1}`}
              className="h-16 w-full border border-white/15 bg-ink text-center font-display text-2xl font-bold text-paper transition-colors focus:border-acid focus:outline-none"
            />
          ))}
        </div>
        {error && (
          <p data-testid="admin-login-error" className="mt-4 font-mono text-[11px] uppercase tracking-[0.15em] text-red-400">
            {error}
          </p>
        )}
        <button
          type="button"
          data-testid="admin-login-button"
          onClick={() => submit(digits.join(""))}
          disabled={busy || digits.join("").length < 4}
          className="mt-8 w-full bg-acid py-4 font-mono text-xs font-bold uppercase tracking-[0.25em] text-ink transition-colors hover:bg-paper disabled:opacity-40"
        >
          {busy ? "Checking…" : "Enter"}
        </button>
      </div>
      <a
        href="/"
        data-testid="admin-login-back-home"
        className="mt-8 font-mono text-[10px] uppercase tracking-[0.25em] text-paper/40 transition-colors hover:text-acid"
      >
        ← Back to site
      </a>
    </div>
  );
}
